import React, { useState } from 'react';
import {
    Box, Typography, IconButton, TextField,
    FormControl, InputLabel, Select, MenuItem, Button
} from '@mui/material';
import { Edit, Delete, Save, Cancel, Menu } from '@mui/icons-material';

const LessonManager = ({ lessons, onLocalUpdate, onDeleteLesson, courseId }) => {
    const [editingLesson, setEditingLesson] = useState(null);
    const [tempLesson, setTempLesson] = useState({});
    const [deletingIndex, setDeletingIndex] = useState(null);

    const handleEdit = (lesson, index) => {
        setEditingLesson(index);
        setTempLesson({ ...lesson });
    };

    const handleSave = (index) => {
        const updatedLessons = [...lessons];
        const cleanedSlug = tempLesson.slug
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^\w-]+/g, '');
            
        updatedLessons[index] = {
            ...tempLesson,
            slug: cleanedSlug
        };
        onLocalUpdate(updatedLessons);
        setEditingLesson(null);
    };

    const handleCancel = () => {
        setEditingLesson(null);
    };

    const handleDelete = async (index) => {
        const lessonToDelete = lessons[index];
        setDeletingIndex(index);
        
        try {
            // Call the parent's delete function to handle S3 deletion
            if (onDeleteLesson && lessonToDelete.id) {
                await onDeleteLesson(lessonToDelete.id);
            }
            
            // Then update the local state
            const updatedLessons = lessons.filter((_, i) => i !== index);
            const reorderedLessons = updatedLessons.map((lesson, idx) => ({
                ...lesson,
                order: idx + 1,
                id: idx + 1 // Update IDs sequentially
            }));
            onLocalUpdate(reorderedLessons);
        } catch (error) {
            console.error("Error deleting lesson:", error);
            alert("Failed to delete lesson. Please try again.");
        } finally {
            setDeletingIndex(null);
        }
    };

    const handleDragStart = (e, index) => {
        e.dataTransfer.setData('lessonIndex', index);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e, targetIndex) => {
        const sourceIndex = e.dataTransfer.getData('lessonIndex');
        if (sourceIndex === targetIndex) return;

        const reorderedLessons = [...lessons];
        const [movedLesson] = reorderedLessons.splice(sourceIndex, 1);
        reorderedLessons.splice(targetIndex, 0, movedLesson);

        const updatedLessons = reorderedLessons.map((lesson, index) => ({
            ...lesson,
            order: index + 1
        }));

        onLocalUpdate(updatedLessons);
    };

    return (
        <Box>
            {lessons.length === 0 ? (
                <Typography sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                    No lessons added yet
                </Typography>
            ) : (
                <Box>
                    {lessons.map((lesson, index) => (
                        <Box
                            key={lesson.id || index} // Use lesson.id if available
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, index)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '10px',
                                marginBottom: '10px',
                                backgroundColor: 'white',
                                borderRadius: '4px',
                                border: '1px solid #e0e0e0',
                                cursor: 'move',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                        >
                            <IconButton sx={{ marginRight: '10px', cursor: 'move' }}>
                                <Menu />
                            </IconButton>

                            {editingLesson === index ? (
                                <Box sx={{ flexGrow: 1 }}>
                                    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                                        <TextField
                                            size="small"
                                            label="Title"
                                            value={tempLesson.title || ''}
                                            onChange={(e) => setTempLesson({ ...tempLesson, title: e.target.value })}
                                            sx={{ flex: 2, fontFamily: "'Satoshi', sans-serif" }}
                                        />
                                        <FormControl size="small" sx={{ flex: 1 }}>
                                            <InputLabel>Status</InputLabel>
                                            <Select
                                                value={tempLesson.status || 'Draft'}
                                                label="Status"
                                                sx={{ fontFamily: "'Satoshi', sans-serif" }}
                                                onChange={(e) => setTempLesson({ ...tempLesson, status: e.target.value })}
                                            >
                                                <MenuItem value="Draft">Draft</MenuItem>
                                                <MenuItem value="Published">Published</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <TextField
                                            size="small"
                                            label="Slug"
                                            value={tempLesson.slug || ''}
                                            onChange={(e) => setTempLesson({ ...tempLesson, slug: e.target.value })}
                                            sx={{ flex: 1, fontFamily: "'Satoshi', sans-serif" }}
                                            helperText={`/${courseId}/${tempLesson.slug || ''}`}
                                        />
                                        <IconButton onClick={() => handleSave(index)} color="primary">
                                            <Save />
                                        </IconButton>
                                        <IconButton onClick={handleCancel}>
                                            <Cancel />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="body1" fontWeight="bold" sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                                            {lesson.title} {lesson.status === 'Draft' && '(Draft)'}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                                            /{courseId}/{lesson.slug} • Order: {lesson.order} • ID: {lesson.id || 'N/A'}
                                        </Typography>
                                    </Box>
                                    <IconButton onClick={() => handleEdit(lesson, index)}>
                                        <Edit />
                                    </IconButton>
                                    <IconButton 
                                        onClick={() => handleDelete(index)} 
                                        color="error"
                                        disabled={deletingIndex === index}
                                    >
                                        <Delete />
                                    </IconButton>
                                </>
                            )}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default LessonManager;